/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, Consts, DownloadService, HttpService, I18nService, TimeService } from '@dm/core';
import { type NameAndUnit } from 'app/common/types/mo';
import { type Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ScheduleReportService {
    constructor(
        private i18nService: I18nService,
        private httpService: HttpService,
        private timeService: TimeService,
        private downloadService: DownloadService,
    ) { }

    /**
     * 查询Pms Lun配置信息
     */
    getPmsLun(opt?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('pmsLun.get', 'V2');
        return this.httpService.get(rest, { ...opt, doException: false });
    }

    // 获取任务
    getTask(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTask.getBatch', 'V2');
        return this.httpService.get(rest, options);
    }

    // 获取任务Rest
    getTaskRest(): Rest {
        return this.httpService.makeUrl('reportTask.getBatch', 'V2');
    }

    // 创建任务
    createTask(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTask.create', 'V2');
        return this.httpService.post(rest, params, options);
    }

    // 修改任务
    modifyTask(params, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTask.modify', 'V2');
        return this.httpService.put(rest, params, options);
    }

    // 删除任务
    deleteTask(id, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const url = this.httpService.makeUrl('reportTask.delete', 'V2').format(id);
        return this.httpService.delete(url, options);
    }

    // 获取数据文件信息
    getReportFile(taskId, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTaskFile.getBatch', 'V2').setParams({ task_id: taskId });
        return this.httpService.get(rest, options);
    }

    // 删除数据文件
    deleteReportFile(taskId, logId, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTaskFile.getBatch', 'V2').setParams({ task_id: taskId, log_id: logId });
        return this.httpService.delete(rest, options);
    }

    // 导出数据文件
    exportFile(taskId, logId, fileName): Observable<boolean> {
        const rest = this.httpService.makeUrl('reportTaskFile.export', 'V2').setParams({ task_id: taskId, log_id: logId });
        return this.downloadService.download(rest, fileName);
    }

    // 立即导出数据文件
    exportNow(taskId): Observable<boolean> {
        const rest = this.httpService.makeUrl('reportTaskFile.exportNow', 'V2').setParams({ task_id: taskId });
        return this.downloadService.download(rest);
    }

    // 立即执行
    executeExportNow(taskId, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('reportTaskFile.exportNow', 'V2').setParams({ task_id: taskId });
        return this.httpService.get(rest, options);
    }

    /**
     * @description
     * 获得指标的name，动态转换单位的指标不显示单位
     * @param {Number} key [指标的枚举值]
     * @return {String} [指标的name]
     */
    getIndicatorName(key): string {
        return this.getIndicator(key).name;
    }

    /**
     * @description
     * 获得指标的unit
     * @param {Number} key [指标的枚举值]
     * @return {String} [指标的name]
     */
    getIndicatorUnit(key: string): string {
        return this.getIndicator(key).unit;
    }

    /**
     * @description
     * 获得指标的name和unit，动态转换单位的指标不显示单位
     * @param {Number} key [指标的枚举值]
     * @return {Object} [指标的name和unit]
     */
    getIndicator(key: string): NameAndUnit {
        const name = this.i18nService.get(`monitor.performance_term_indicator_${key}`) === `monitor.performance_term_indicator_${key}` ?
            this.i18nService.get('monitor.performance_term_indicator_unknow') :
            this.i18nService.get(`monitor.performance_term_indicator_${key}`);
        const tempObj = this.splitIndicatorName(name);
        // 如果不需要动态转换单位,或者配置需要展示全部单位，则显示单位
        if (!this.needAutoConvert(tempObj.unit)) {
            tempObj.name = name;
        }
        return tempObj;
    }

    /**
     * @description
     * 分割指标项的name和unit
     * @param {String} name [指标的i18n]
     * @return {Object} [指标的name和unit]
     */
    splitIndicatorName(name: string): NameAndUnit {
        let dataName;
        let dataUnit;
        const tempUnit = name.match(/\(.*\)+/g);
        if (tempUnit) {
            dataName = name.match(/.*(?=\()/g)[0];
            dataUnit = tempUnit[0].substring(1, tempUnit[0].length - 1);
        } else {
            dataName = name;
            dataUnit = '';
        }
        return {
            name: dataName,
            unit: dataUnit,
        };
    }

    /**
     * @description
     * 是否需要动态转换单位
     * @param {String} unit [单位]
     * @return {Bool}
     */
    needAutoConvert(unit: string): boolean {
        return this.isCapacityUnit(unit) || this.isTimeUnit(unit);
    }

    /**
     * @description
     * 是否容量单位
     * @param {String} unit [单位]
     * @return {Bool}
     */
    isCapacityUnit(unit: string): boolean {
        const reg = [Consts.BYTE, Consts.KB, Consts.MB, Consts.GB, Consts.TB, Consts.PB,
        Consts.EB, Consts.ZB, Consts.YB, Consts.BB, Consts.NB, Consts.DB].join('|');
        return new RegExp(`\\b(${reg})\\b`, 'i').test(unit);
    }

    /**
     * @description
     * 是否时间单位
     * @param {String} unit [单位]
     * @return {Bool}
     */
    isTimeUnit(unit: string): boolean {
        const reg = ['us', 'ms', 's'].join('|');
        return new RegExp('^(' + reg + ')$', 'i').test(unit);
    }

     // 获得时间字符串
    getTimeString(timeValue: string, tooltip?: string): string {
        // 如果是tip，需要显示时区以及夏令时，如果不是，仅需转换夏令时
        return this.timeService.formatUTC(timeValue, tooltip ? 'YYYY-MM-DD HH:mm:ss' : 'HH:mm/MM-DD');
    }

    // 查询以太网端口信息
    getBatch(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('ethPort.getBatch');
        return this.httpService.get(rest);
    }
}
